import { IUserPublicProfile } from '@koopajs/app';
import { alphabeticalUserSortFunction } from 'utils/alphabeticalUserSortFunction';
import { useLocale } from '@koopajs/react';
import { IParticipant } from 'types';
import { processUsersForInput } from 'utils/processUsersForInput';

export interface IOption {
  id: string;
  label: string;
  subLabel?: string;
  srcAvatar?: string;
}

interface IDefaultValue {
  id: string;
  label: string;
}

interface IGetUserOptionHelpersResponse {
  getUsersOptions: (users: IUserPublicProfile[], committeeTitles: Record<string, string>) => IOption[];
  getMeetingReviewersOptions: (
    users: IUserPublicProfile[],
    meetingReviewersIds: string[],
    committeeTitles: Record<string, string>
  ) => IOption[];
  getDefaultParticipants: (participants: IParticipant[], users: IUserPublicProfile[]) => IOption[];
  getDefaultMeetingReviewers: (meetingReviewersIds: string[], users: IUserPublicProfile[]) => IOption[];
}

export const getResourcePickerOptionsForMeeting = (): IGetUserOptionHelpersResponse => {
  const { t } = useLocale();

  const mapUserToOption = (
    user: IUserPublicProfile | undefined,
    id: string,
    titles?: Record<string, string>
  ): IOption => {
    const titleKey = user && titles?.[id];
    return {
      id,
      label: user?.username || '',
      subLabel: titleKey ? t(`common:committeeMemberTitles.${titleKey}`) : undefined,
      srcAvatar: user?.profilePicture
    };
  };

  // Resource picker available options

  const getUsersOptions = (users: IUserPublicProfile[], committeeTitles: Record<string, string>): IOption[] =>
    [...users]
      .sort(alphabeticalUserSortFunction)
      .map((user) => mapUserToOption(user, user.id, committeeTitles));

  const getMeetingReviewersOptions = (
    users: IUserPublicProfile[],
    meetingReviewersIds: string[],
    committeeTitles: Record<string, string>
  ): IOption[] => {
    return meetingReviewersIds.map((userId) => {
      const user = users.find((user) => userId === user.id);
      return mapUserToOption(user, userId, committeeTitles);
    });
  };

  // Resource picker default values

  const getDefaultParticipants = (
    participants: IParticipant[],
    users: IUserPublicProfile[]
  ): IDefaultValue[] => {
    return processUsersForInput({
      users,
      selectedUserIds: participants?.map((user) => user.userId)
    });
  };

  const getDefaultMeetingReviewers = (
    meetingReviewersIds: string[],
    users: IUserPublicProfile[]
  ): IDefaultValue[] => {
    return processUsersForInput({
      users,
      selectedUserIds: meetingReviewersIds
    });
  };

  return {
    getUsersOptions,
    getMeetingReviewersOptions,
    getDefaultParticipants,
    getDefaultMeetingReviewers
  };
};
